import { FC, useCallback, useEffect, useState } from 'react'
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react'
import { ArrowForwardIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons'
import { useNavigate } from 'react-router-dom'
import {
  CalendarEvent,
  fetchSessionAdjustment,
  SessionAdjustment,
  SessionAdjustmentPayload,
  saveSessionAdjustment,
} from '../../services/calendar'
import { EventFrequency } from '../../enum/provider/calendar/EventFrequency'
import { durationLabel, eventBarClass, longDateLabel } from './calendar-layout'

interface CalendarEventDetailModalProps {
  event: CalendarEvent | null
  isOpen: boolean
  isDeleting: boolean
  onClose: () => void
  onEdit: (event: CalendarEvent) => void
  onDelete: (event: CalendarEvent) => void
  /** Chamado depois que o criativo ajusta duração/horário do ensaio. */
  onSessionAdjusted?: () => void
}

const CalendarEventDetailModal: FC<CalendarEventDetailModalProps> = ({
  event,
  isOpen,
  isDeleting,
  onClose,
  onEdit,
  onDelete,
  onSessionAdjusted,
}) => {
  const navigate = useNavigate()
  const toast = useToast()

  // ── Ajuste do ensaio ──────────────────────────────────────────────────────
  //
  // Só existe quando o cliente liberou este criativo (`can_edit_session_*`).
  // Os valores vêm do banco ao abrir, e não do bloco desenhado na agenda: o
  // bloco tem piso de 30 min, então usá-lo como valor inicial faria o criativo
  // salvar uma duração diferente da atual sem ter tocado no campo.
  const [adjustment, setAdjustment] = useState<SessionAdjustment | null>(null)
  const [duration, setDuration] = useState('')
  const [scheduledFor, setScheduledFor] = useState('')
  const [loadingAdjustment, setLoadingAdjustment] = useState(false)
  const [savingAdjustment, setSavingAdjustment] = useState(false)
  const [adjustmentError, setAdjustmentError] = useState<string | null>(null)

  const sessionId = event?.session_id ?? null
  const canAdjust = Boolean(
    sessionId && (event?.can_edit_session_duration || event?.can_edit_session_schedule),
  )

  useEffect(() => {
    if (!isOpen || !canAdjust || !sessionId) {
      setAdjustment(null)
      setAdjustmentError(null)
      return
    }

    let active = true
    setLoadingAdjustment(true)

    fetchSessionAdjustment(sessionId)
      .then(data => {
        if (!active) return
        setAdjustment(data)
        setDuration(String(data.duration_minutes))
        setScheduledFor(data.scheduled_for ?? '')
        setAdjustmentError(null)
      })
      .catch(() => {
        if (!active) return
        setAdjustment(null)
        setAdjustmentError('Não foi possível carregar os dados do ensaio.')
      })
      .finally(() => {
        if (active) setLoadingAdjustment(false)
      })

    return () => {
      active = false
    }
  }, [isOpen, canAdjust, sessionId])

  /**
   * Manda só o que mudou. Cada campo é aplicado no servidor sob a SUA
   * permissão — mandar o horário sem poder mexer nele volta 403, então não
   * adianta o formulário insistir.
   */
  const submitAdjustment = useCallback(async () => {
    if (!sessionId || !adjustment) {
      return
    }

    const payload: SessionAdjustmentPayload = {}

    if (adjustment.can_edit_duration && Number(duration) !== adjustment.duration_minutes) {
      payload.duration_minutes = Number(duration)
    }

    if (adjustment.can_edit_schedule && scheduledFor && scheduledFor !== adjustment.scheduled_for) {
      payload.scheduled_for = scheduledFor
    }

    if (Object.keys(payload).length === 0) {
      toast({
        title: 'Nada foi alterado no ensaio.',
        status: 'info',
        position: 'top-right',
        duration: 4000,
        isClosable: true,
      })
      return
    }

    setSavingAdjustment(true)
    setAdjustmentError(null)

    try {
      await saveSessionAdjustment(sessionId, payload)
      toast({
        title: 'Ensaio atualizado — sua agenda já foi sincronizada.',
        status: 'success',
        position: 'top-right',
        duration: 6000,
        isClosable: true,
      })
      onClose()
      onSessionAdjusted?.()
    } catch (reason: any) {
      const response = reason?.response
      const firstError = response?.data?.errors
        ? (Object.values(response.data.errors)[0] as string[])?.[0]
        : null

      setAdjustmentError(firstError ?? response?.data?.message ?? 'Não foi possível salvar o ajuste.')
    } finally {
      setSavingAdjustment(false)
    }
  }, [sessionId, adjustment, duration, scheduledFor, toast, onClose, onSessionAdjusted])

  if (!event) {
    return null
  }

  // Ensaios são gerenciados pela operação: aparecem na agenda, mas mudar o
  // horário deles aqui deixaria o criativo e o cliente com combinados diferentes.
  const isEditable = !event.session_item_id

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <div className={`h-1.5 w-full ${eventBarClass(event.color)}`} />
        <ModalHeader pb={1}>
          <Text fontSize="lg" fontWeight="semibold" color="gray.700">
            {event.display_title}
          </Text>
          <Text fontSize="xs" color="gray.500" fontWeight="medium">
            {event.type_text}
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Flex direction="column" gap={3}>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-sm capitalize text-slate-700">{longDateLabel(event.date)}</p>
              {event.is_flexible ? (
                <p className="mt-1 text-sm text-slate-700">Duração: {durationLabel(event.duration_minutes)}</p>
              ) : (
                event.starts_at && (
                  <p className="mt-1 text-sm text-slate-700">
                    {event.starts_at} — {event.ends_at}
                  </p>
                )
              )}
              {event.recurrence_type !== EventFrequency.DOES_NOT_REPEAT && (
                <p className="mt-1 text-xs text-slate-500">Repete: {event.recurrence_text}</p>
              )}
            </div>

            {event.session_id && (
              <div className="rounded-lg border border-slate-200 p-3">
                <p className="mb-2 text-xs uppercase tracking-wide text-slate-400">Ensaio vinculado</p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Nº</span>
                    <span className="font-semibold text-slate-700">#{event.session_id}</span>
                  </div>
                  {event.client_name && (
                    <div className="flex items-center justify-between gap-3">
                      <span className="shrink-0 text-slate-500">Cliente</span>
                      <span className="truncate text-slate-700">{event.client_name}</span>
                    </div>
                  )}
                  {event.session_address && (
                    <div className="border-t border-slate-100 pt-1.5">
                      <p className="text-xs text-slate-500">Endereço</p>
                      <p className="mt-0.5 text-xs text-slate-700">{event.session_address}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {canAdjust && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="mb-2 text-xs uppercase tracking-wide text-slate-400">Ajustar ensaio</p>

                {loadingAdjustment ? (
                  <Flex justify="center" py={2}>
                    <Spinner size="sm" />
                  </Flex>
                ) : (
                  <>
                    {adjustment?.can_edit_duration && (
                      <label className="block">
                        <span className="text-xs font-medium text-slate-600">Duração (minutos)</span>
                        <input
                          type="number"
                          min={15}
                          max={720}
                          step={5}
                          value={duration}
                          onChange={e => setDuration(e.target.value)}
                          className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
                        />
                        <span className="mt-1 block text-[11px] text-slate-400">
                          {adjustment.is_manual_duration
                            ? 'Duração definida manualmente para este ensaio.'
                            : 'Hoje a duração é calculada pelos serviços do ensaio. Ao salvar, este valor passa a valer.'}
                        </span>
                      </label>
                    )}

                    {adjustment?.can_edit_schedule && (
                      <label className="mt-3 block">
                        <span className="text-xs font-medium text-slate-600">Data e hora</span>
                        <input
                          type="datetime-local"
                          value={scheduledFor}
                          onChange={e => setScheduledFor(e.target.value)}
                          className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
                        />
                      </label>
                    )}

                    {adjustmentError && <p className="mt-2 text-xs text-red-500">{adjustmentError}</p>}

                    {adjustment && (
                      <Button
                        mt={3}
                        size="sm"
                        w="full"
                        colorScheme="blue"
                        isLoading={savingAdjustment}
                        onClick={submitAdjustment}
                      >
                        Salvar ajuste
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}

            {event.notes && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                <p className="text-sm italic text-slate-700">{event.notes}</p>
              </div>
            )}
          </Flex>
        </ModalBody>

        <ModalFooter gap={2}>
          {event.session_id && (
            <Button
              size="sm"
              colorScheme="blue"
              rightIcon={<ArrowForwardIcon />}
              onClick={() => {
                onClose()
                navigate(`/ensaios/${event.session_id}`)
              }}
            >
              Ver ensaio
            </Button>
          )}
          {isEditable && (
            <>
              <Button
                size="sm"
                variant="outline"
                colorScheme="red"
                leftIcon={<DeleteIcon />}
                isLoading={isDeleting}
                onClick={() => onDelete(event)}
              >
                Excluir
              </Button>
              <Button size="sm" colorScheme="blue" leftIcon={<EditIcon />} onClick={() => onEdit(event)}>
                Editar
              </Button>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default CalendarEventDetailModal
